import index from "@/views/index";
import view404 from "@/views/view404";
import store from "@/store";
import localStore from "@/utils/localStore";
import {
  defaultPageBasedOnPermissions,
  permissionsToStringMap as permissions,
  userTypes,
  allUserTypes,
} from "@/middleware/pageAccessManager";

import noPondsView from "@/views/noPondsView";

/* user signUp and its related components */
const v2Dashboard = () =>
  import(/* webpackChunkName: "v2Dashboard" */ "@/views/v2Views/v2Dashboard");

export const settings = () =>
  import(/* webpackChunkName: "settings" */ "@/views/manage/settings");
export const resources = () =>
  import(/* webpackChunkName: "resources" */ "@/views/manage/resources");
export const reports = () =>
  import(/* webpackChunkName: "reports" */ "@/views/manage/reports");
export const manualFeeding = () =>
  import(
    /* webpackChunkName: "manualFeeding" */ "@/views/culture/manualFeeding"
  );
export const allStocks = () =>
  import(/* webpackChunkName: "allStocks" */ "@/views/stock/allStocks");
export const stockMedicine = () =>
  import(/* webpackChunkName: "stockMedicine" */ "@/views/stock/medicine");
export const stockFeed = () =>
  import(/* webpackChunkName: "stockFeed" */ "@/views/stock/feed");
export const gatewayView = () =>
  import(/* webpackChunkName: "gatewayView" */ "@/views/manage/gateway");
// Overview Menu Group
export const pondsMap = () =>
  import(
    /* webpackChunkName: "pondsMapAndFeeding" */ "@/views/overview/pondsMap"
  );
export const Battery = () =>
  import(/* webpackChunkName: "battery" */ "@/views/overview/battery");

export const pondDetails = () =>
  import(/* webpackChunkName: "pondDetails" */ "@/views/overview/pondDetails");
export const feedView = () =>
  import(/* webpackChunkName: "feedView" */ "@/views/overview/feedView");

// Culture Menu Group
export const abw = () =>
  import(/* webpackChunkName: "abw" */ "@/views/culture/abw");
export const harvestYield = () =>
  import(/* webpackChunkName: "harvestYield" */ "@/views/culture/harvestYield");
export const pondFeeding = () =>
  import(/* webpackChunkName: "pondFeeding" */ "@/views/culture/pondFeeding");
export const feeding = () =>
  import(
    /* webpackChunkName: "pondsMapAndFeeding" */ "@/views/culture/feeding"
  );
export const labTests = () =>
  import(/* webpackChunkName: "labTests" */ "@/views/culture/labTests");
export const cultureMedicine = () =>
  import(/* webpackChunkName: "cultureMedicine" */ "@/views/culture/medicine");
export const userProfile = () =>
  import(
    /* webpackChunkName: "userProfile" */ "@/components/settings/userProfile"
  );

const signout = () => {
  store.dispatch("auth/signOut").catch((err) => {
    throw err;
  });
};

const defaultPagesBasedOnUserType = (userType, parsedUserPermissions) => {
  const route = defaultPageBasedOnPermissions(parsedUserPermissions);
  switch (userType) {
    case userTypes.super_admin:
    case userTypes.no_access:
      signout();
      return "/sign-in";
    case userTypes.admin:
    case userTypes.manager:
    case userTypes.pond_owner:
      return "/user/dashboard";
    default:
      if (route) {
        return route;
      }
      signout();
      return "/sign-in";
  }
};

const validateForSetupRoute = async function(to, from, next) {
  try {
    const currUserType = localStore.getItem("curr-user-type");
    const userId = localStore.getItem(`${currUserType}-id`);
    if (
      currUserType === undefined ||
      !allUserTypes.includes(currUserType) ||
      !userId
    ) {
      signout();
      return next("/sign-in");
    }
    await store.dispatch("user/fetchUserProfile");
    const location = await store.dispatch("user/fetchUserLocations");
    console.log(location);
    await store.dispatch("user/updateCurrUserLocation", location);
    const hasLocations = Object.keys(store.getters["user/getUserLocationsObj"])
      .length;
    if (to.meta.dependOnLocations && !hasLocations) {
      return next("/user/no-ponds");
    }
    return next();
  } catch (err) {
    console.error(
      "Check the flow logic and user permissions of the router",
      err
    );
    signout();
    return next("/sign-in");
  }
};
const doesUserCanAccess = function(to, from, next) {
  try {
    const currUserType = localStore.getItem("curr-user-type");
    const userId = localStore.getItem(`${currUserType}-id`);
    if (!currUserType || !userId) {
      signout();
      return next("/sign-in");
    }
    const userProfileObj = store.getters["user/getUserProfile"];
    if (!userProfileObj) {
      return next("/");
    }
    const parsedUserPermissions = store.getters["user/getViewPermissions"];
    const hasLocations = Object.keys(store.getters["user/getUserLocationsObj"])
      .length;
    if (to.meta.dependOnLocations && !hasLocations) {
      return next("/user/no-ponds");
    }
    const prsntUserType = currUserType;
    const keyToValidateAccess = to.meta.keyToValidateAccess;
    const methodToValidate =
      keyToValidateAccess === "PERMISSIONS"
        ? (x) => parsedUserPermissions[x]
        : (x) => x === prsntUserType;
    const canViewItem = to.meta[keyToValidateAccess].some(methodToValidate);
    if (!canViewItem) {
      return next(
        defaultPagesBasedOnUserType(prsntUserType, parsedUserPermissions)
      );
    }
    return next();
  } catch (err) {
    console.error(
      "Check the flow logic and user permissions of the router",
      err
    );
  }
};

export default {
  path: "/user",
  component: index,
  children: [
    {
      path: "/",
      redirect: "dashboard",
    },
    {
      path: "dashboard",
      name: "dashboard",
      component: v2Dashboard,
      meta: {
        keyToValidateAccess: "PERMISSIONS",
        PERMISSIONS: [
          permissions.MANAGE_OVERVIEW,
          permissions.MANAGE_CULTURE,
          permissions.MANAGE_DEVICES,
          permissions.MANAGE_ABW,
          permissions.MANAGE_HARVEST,
        ],
        dependOnLocations: true,
        langKey: "Comn_dashboard",
      },
      beforeEnter: doesUserCanAccess,
    },
    {
      path: "map-view",
      name: "map",
      meta: {
        keyToValidateAccess: "PERMISSIONS",
        PERMISSIONS: [permissions.MANAGE_CULTURE, permissions.MANAGE_DEVICES],
        dependOnLocations: false,
        langKey: "Comn_map",
      },
      component: pondsMap,
      beforeEnter: doesUserCanAccess,
    },
    {
      path: "pond-details",
      name: "pondsDetails",
      component: pondDetails,
      name: "ponds",
      meta: {
        keyToValidateAccess: "PERMISSIONS",
        PERMISSIONS: [
          permissions.MANAGE_ABW,
          permissions.MANAGE_CULTURE,
          permissions.MANAGE_SCHEDULES,
          permissions.MANAGE_MEDICINE,
          permissions.MANAGE_LAB_TESTS,
        ],
        dependOnLocations: true,
        langKey: "Comn_ponds",
      },
      beforeEnter: doesUserCanAccess,
    },

    {
      path: "battery",
      name: "battery",
      component: Battery,
      meta: {
        keyToValidateAccess: "PERMISSIONS",
        PERMISSIONS: [permissions.MANAGE_CULTURE, permissions.MANAGE_DEVICES],
        dependOnLocations: true,
        langKey: "Comn_battery",
      },
      beforeEnter: doesUserCanAccess,
    },

    {
      path: "feeding",
      component: feeding,
      name: "feeding",
      meta: {
        keyToValidateAccess: "PERMISSIONS",
        PERMISSIONS: [permissions.MANAGE_CULTURE, permissions.MANAGE_SCHEDULES],
        dependOnLocations: true,
        langKey: "Comn_feeding",
      },
      beforeEnter: doesUserCanAccess,
    },
    {
      path: "culture-medicine",
      component: cultureMedicine,
      name: "cultureMedicine",
      meta: {
        keyToValidateAccess: "PERMISSIONS",
        PERMISSIONS: [permissions.MANAGE_MEDICINE],
        dependOnLocations: true,
        langKey: "Comn_culture_medicine",
      },
      beforeEnter: doesUserCanAccess,
    },
    {
      path: "abw",
      component: abw,
      name: "abw",
      meta: {
        keyToValidateAccess: "PERMISSIONS",
        PERMISSIONS: [permissions.MANAGE_ABW],
        dependOnLocations: true,
        langKey: "ABW_abw",
      },
      beforeEnter: doesUserCanAccess,
    },
    {
      path: "lab-tests",
      component: labTests,
      name: "labTests",
      meta: {
        keyToValidateAccess: "PERMISSIONS",
        PERMISSIONS: [permissions.MANAGE_LAB_TESTS],
        dependOnLocations: true,
        langKey: "LabTst_lab_test",
      },
      beforeEnter: doesUserCanAccess,
    },
    {
      path: "yield",
      component: harvestYield,
      name: "yield",
      meta: {
        keyToValidateAccess: "PERMISSIONS",
        PERMISSIONS: [permissions.MANAGE_HARVEST],
        dependOnLocations: true,
        langKey: "Comn_yield",
      },
      beforeEnter: doesUserCanAccess,
    },

    {
      path: "stock",
      name: "stock",
      meta: {
        keyToValidateAccess: "PERMISSIONS",
        PERMISSIONS: [permissions.MANAGE_STOCK],
        dependOnLocations: false,
        langKey: "Stock_stock",
      },
      component: allStocks,
      beforeEnter: doesUserCanAccess,
    },

    {
      path: "resources",
      name: "resources",
      meta: {
        keyToValidateAccess: "PERMISSIONS",
        PERMISSIONS: [
          permissions.MANAGE_MEDICINE,
          permissions.MANAGE_LAB_TESTS,
          permissions.MANAGE_STOCK,
        ],
        dependOnLocations: false,
        langKey: "Comn_resources",
      },
      component: resources,
      beforeEnter: doesUserCanAccess,
    },
    {
      path: "reports",
      name: "reports",
      component: reports,
      meta: {
        keyToValidateAccess: "USER_TYPES",
        USER_TYPES: [
          userTypes.admin,
          userTypes.pond_manager,
          userTypes.pond_owner,
        ],
        dependOnLocations: true,
        langKey: "Comn_reports",
      },
      beforeEnter: doesUserCanAccess,
    },
    {
      path: "gateway",
      component: gatewayView,
      name: "gatewayView",
      meta: {
        keyToValidateAccess: "PERMISSIONS",
        PERMISSIONS: [
          permissions.MANAGE_CULTURE,
          permissions.MANAGE_SCHEDULES,
          permissions.MANAGE_DEVICES,
        ],
        dependOnLocations: true,
        langKey: "Comn_gateway",
      },
      beforeEnter: doesUserCanAccess,
    },
    {
      path: "settings",
      name: "settings",
      component: settings,
      meta: {
        keyToValidateAccess: "USER_TYPES",
        USER_TYPES: allUserTypes,
        dependOnLocations: false,
        langKey: "Comn_settings",
      },
      beforeEnter: doesUserCanAccess,
    },
    {
      path: "no-ponds",
      name: "noPondsView",
      component: noPondsView,
      meta: {
        keyToValidateAccess: "USER_TYPES",
        USER_TYPES: allUserTypes,
        dependOnLocations: false,
        langKey: "PondLogs",
      },
      beforeEnter: doesUserCanAccess,
    },
    {
      path: "404",
      name: "pondlogs404",
      component: view404,
      meta: {
        keyToValidateAccess: "USER_TYPES",
        USER_TYPES: allUserTypes,
        langKey: "PondLogs",
      },
    },
  ],
  beforeEnter: validateForSetupRoute,
};
